import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Calculator, ChevronDown, ClipboardList, Download, Menu, X } from "lucide-react";
import { useCategories, useProducts, useSettings, fileUrl } from "@/lib/api";
import { useApp } from "@/lib/store";
import { track } from "@/lib/seo";

const ABOUT = [["/about/legacy", "Our Legacy"], ["/about/team", "Leadership & Team"], ["/about/achievements", "Achievements & Certifications"]];

function MegaMenu({ onClose }) {
  const { data: cats = [] } = useCategories();
  const { data: prods = [] } = useProducts();
  const { data: s } = useSettings();
  return (
    <div data-testid="products-mega-menu" className="absolute left-0 right-0 top-full bg-[var(--paper)] border-y border-[var(--stone)] shadow-sm">
      <div className="wrap grid grid-cols-4 gap-10 py-10">
        {cats.map((c) => (
          <div key={c.slug}>
            <Link to={`/products/${c.slug}`} onClick={onClose} className="group block">
              <div className="img-frame prod-bg aspect-[16/10] flex items-center justify-center mb-4 border border-[var(--stone)]"><img src={c.image} alt="" className="h-4/5 object-contain" /></div>
              <p className="serif text-xl">{c.name}</p>
            </Link>
            <ul className="mt-3 space-y-1.5 text-sm text-[var(--graphite)]">
              {prods.filter((p) => p.category === c.slug).slice(0, 6).map((p) => (
                <li key={p.slug}><Link onClick={onClose} to={`/products/${p.category}/${p.slug}`} className="hover:text-[var(--brass)] transition-colors">{p.name}</Link></li>
              ))}
            </ul>
            <Link to={`/products/${c.slug}`} onClick={onClose} className="inline-block mt-3 text-sm link">View all {c.count} →</Link>
          </div>
        ))}
        <div className="border-l border-[var(--stone)] pl-10 flex flex-col gap-5">
          <p className="eyebrow">Browse</p>
          <Link to="/products/all" onClick={onClose} data-testid="mega-all-products" className="serif text-2xl hover:text-[var(--brass)] transition-colors">All Products A–Z</Link>
          <Link to="/products" onClick={onClose} className="serif text-2xl hover:text-[var(--brass)] transition-colors">Product Overview</Link>
          {s?.catalogue_url ? (
            <a href={fileUrl(s.catalogue_url)} target="_blank" rel="noreferrer" onClick={() => track("catalogue_download")} data-testid="mega-catalogue" className="btn btn-outline btn-sm w-fit"><Download size={14} /> Download Catalogue (PDF)</a>
          ) : (
            <Link to="/contact?type=General" onClick={onClose} data-testid="mega-catalogue" className="btn btn-outline btn-sm w-fit"><Download size={14} /> Request Catalogue (PDF)</Link>
          )}
        </div>
      </div>
    </div>
  );
}

const navCls = ({ isActive }) => `relative py-2 text-[14px] tracking-wide transition-colors hover:text-[var(--brass)] ${isActive ? "text-[var(--brass)]" : "text-[var(--ink)]"}`;

function MobileNav({ onClose }) {
  const links = [["/", "Home"], ...ABOUT, ["/products", "Products"], ["/products/all", "All Products A–Z"], ["/shipment-calculator", "Shipment Calculator"], ["/careers", "Careers"], ["/media", "Media"], ["/contact", "Contact"]];
  return createPortal(
    <div data-testid="mobile-nav" className="fixed inset-0 z-[60] bg-[var(--ivory)] overflow-y-auto">
      <div className="wrap flex justify-between items-center h-20">
        <img src="/logo.png" alt="Tijiya Exports" className="h-10" />
        <button data-testid="mobile-nav-close" aria-label="Close menu" onClick={onClose}><X size={26} /></button>
      </div>
      <nav className="wrap flex flex-col gap-4 py-8">
        {links.map(([to, l]) => <Link key={to} to={to} onClick={onClose} className="serif text-3xl border-b border-[var(--stone)] pb-3">{l}</Link>)}
        <Link to="/enquiry" onClick={onClose} className="btn btn-brass mt-6">Request a Quote</Link>
      </nav>
    </div>,
    document.body,
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(null);
  const [mobile, setMobile] = useState(false);
  const { items, setOpen } = useApp();
  const loc = useLocation();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setMenu(null); }, [loc.pathname]);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-40 bg-[rgba(247,244,238,0.95)] backdrop-blur-md border-b transition-[padding,border-color] duration-300 ${scrolled ? "border-[var(--stone)]" : "border-transparent"}`} onMouseLeave={() => setMenu(null)}>
      <div className={`wrap flex items-center justify-between gap-6 transition-[height] duration-300 ${scrolled ? "h-16" : "h-24"}`}>
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
          <img src="/logo.png" alt="Tijiya Exports" className={`transition-[height] duration-300 ${scrolled ? "h-9" : "h-12"}`} />
          <span className="hidden sm:block leading-tight"><span className="serif text-xl block">Tijiya Exports</span><span className="text-[10px] tracking-[0.18em] text-[var(--graphite)] uppercase">Est. 1987</span></span>
        </Link>
        <nav className="hidden xl:flex items-center gap-7" aria-label="Main">
          <NavLink to="/" end className={navCls}>Home</NavLink>
          <div className="relative" onMouseEnter={() => setMenu("about")}>
            <button data-testid="nav-about" aria-expanded={menu === "about"} onClick={() => setMenu(menu === "about" ? null : "about")} className="flex items-center gap-1 text-[14px] py-2 hover:text-[var(--brass)] transition-colors">About Us <ChevronDown size={14} /></button>
            {menu === "about" && (
              <div className="absolute top-full left-0 pt-3"><div className="card py-3 w-64 shadow-sm">
                {ABOUT.map(([to, l]) => <Link key={to} to={to} data-testid={`nav-${to.split("/").pop()}`} className="block px-5 py-2 text-sm hover:text-[var(--brass)] hover:bg-[var(--ivory)] transition-colors">{l}</Link>)}
              </div></div>
            )}
          </div>
          <div onMouseEnter={() => setMenu("products")}>
            <button data-testid="nav-products" aria-expanded={menu === "products"} onClick={() => setMenu(menu === "products" ? null : "products")} className="flex items-center gap-1 text-[14px] py-2 hover:text-[var(--brass)] transition-colors">Products <ChevronDown size={14} /></button>
          </div>
          <NavLink to="/shipment-calculator" data-testid="nav-calculator" className={(a) => `${navCls(a)} flex items-center gap-1.5`} onMouseEnter={() => setMenu(null)}><Calculator size={15} /> Shipment Calculator</NavLink>
          <NavLink to="/careers" data-testid="nav-careers" className={navCls} onMouseEnter={() => setMenu(null)}>Careers</NavLink>
          <NavLink to="/media" data-testid="nav-media" className={navCls} onMouseEnter={() => setMenu(null)}>Media</NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={navCls} onMouseEnter={() => setMenu(null)}>Contact</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="rfq-basket-button" aria-label="Open enquiry list" onClick={() => setOpen(true)} className="relative p-2 hover:text-[var(--brass)] transition-colors">
            <ClipboardList size={21} />
            {items.length > 0 && <span data-testid="rfq-count" className="absolute -top-0.5 -right-0.5 bg-[var(--brass)] text-[var(--paper)] text-[10px] w-[18px] h-[18px] rounded-full flex items-center justify-center tabular">{items.length}</span>}
          </button>
          <Link to="/enquiry" data-testid="header-rfq-button" className="btn btn-brass btn-sm hidden sm:inline-flex">Request a Quote</Link>
          <button data-testid="mobile-nav-open" aria-label="Open menu" className="xl:hidden p-2" onClick={() => setMobile(true)}><Menu size={24} /></button>
        </div>
      </div>
      {menu === "products" && <MegaMenu onClose={() => setMenu(null)} />}
      {mobile && <MobileNav onClose={() => setMobile(false)} />}
    </header>
  );
}
